<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useQuasar } from 'quasar';
import StudentLinkTable from '@/components/Reports/StudentLinkTable.vue';
import StudentRepository from '@/repositories/StudentRepository';
import { type FlightSchoolCourseStudent, printableStudentFromCourseStudent } from '@/types/FlightSchoolStudent';
import { type EnrollmentRow, searchTableColumns } from '@/types/EnrollmentTable';

const students = ref<FlightSchoolCourseStudent[]>([]);
const studentRows = ref<EnrollmentRow[]>([]);
const loading = ref<boolean>(true);
const $q = useQuasar();

const loadStudents = async () => {
    students.value = await StudentRepository.getAllLockedOutStudents()
        .catch((error: unknown) => {
            const message = error instanceof Error ? error.message : '';
            $q.notify({
                color: 'negative',
                icon: 'warning',
                message: `An error occured: ${message}`,
            });

            return [];
        })
        .finally(() => { loading.value = false; });

    studentRows.value = students.value.map(
        (student: FlightSchoolCourseStudent): EnrollmentRow => ({
            ...printableStudentFromCourseStudent(student),
            isFocused: false,
        }),
    );
};

onMounted(async () => {
    await loadStudents();
    loading.value = false;
});

</script>
<template>
    <div class="fsum-report">
        <div class="row">
            <div class="col-12 text-center report-header">
                <h3 class="title q-mb-md text-center fsum-generic-header">
                    Lockout Report
                </h3>
                <p class="subtitle">
                    List of students who are currently locked out of their course across all flight schools.
                </p>
            </div>
        </div>
        <div
            class="q-pt-md table-container"
            :class="$q.screen.lt.lg ? 'q-px-md' : 'q-px-xl'"
        >
            <StudentLinkTable
                :rows="studentRows"
                :columns="searchTableColumns"
                :loading="loading"
                :sort-by="searchTableColumns[0].name"
                :invisible-columns="['examStatus']"
                :show-filters="false"
            />
        </div>
    </div>
</template>
<style scoped lang="scss">
div.report-header {
    p.subtitle {
        font-size: 18px;
        font-weight: 500;
        text-align: center;
    }
}

:deep(.fsum-student-row.locked-out) {
    background-color: var(--fsum-white);
}

</style>
